/**
 * Side-panel forms: meta/master, layer list with add/remove, per-layer
 * parameter forms, and the segment editor (type-to-dimension numeric entry).
 * Plain DOM, rebuilt on state change — presets are small, so full rebuild is
 * simpler than diffing and plenty fast.
 */

import type { Layer, Preset, Goal, Curve, NoiseColor, LayerType, Waveform } from "../../schema/types.ts";
import { isSegmentedLayer } from "../../schema/types.ts";
import { AMBIENCE_MANIFEST } from "../dsp/ambience.ts";
import type { Store } from "./store.ts";

const GOALS: Goal[] = ["sleep", "meditate", "focus", "obe"];
const CURVES: Curve[] = ["hold", "linear", "exp"];
const COLORS: NoiseColor[] = ["white", "pink", "brown"];
const WAVEFORMS: Waveform[] = ["sine", "triangle", "square", "sawtooth"];
const DUCKABLE: Exclude<LayerType, "voice">[] = ["binaural", "monaural", "isochronic", "noise", "ambience"];

function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  ...children: Array<Node | string>
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
  el.append(...children);
  return el;
}

function numField(label: string, value: number, onCommit: (v: number) => void, step = "any"): HTMLElement {
  const input = h("input", { type: "number", step, value: String(value) });
  input.addEventListener("change", () => {
    const v = parseFloat(input.value);
    if (Number.isFinite(v)) onCommit(v);
  });
  return h("label", { class: "field" }, label, input);
}

function selField<T extends string>(label: string, value: T, options: readonly T[], onCommit: (v: T) => void): HTMLElement {
  const sel = h("select", {});
  for (const o of options) {
    const opt = h("option", { value: o }, o);
    if (o === value) opt.selected = true;
    sel.append(opt);
  }
  sel.addEventListener("change", () => onCommit(sel.value as T));
  return h("label", { class: "field" }, label, sel);
}

function textField(label: string, value: string, onCommit: (v: string) => void): HTMLElement {
  const input = h("input", { type: "text", value });
  input.addEventListener("change", () => onCommit(input.value));
  return h("label", { class: "field" }, label, input);
}

function uniqueId(preset: Preset, base: string): string {
  let n = 1;
  while (preset.layers.some((l) => l.id === `${base}${n}`)) n++;
  return `${base}${n}`;
}

function defaultLayer(type: LayerType, preset: Preset): Layer {
  const d = preset.meta.durationSec;
  const seg = { t0: 0, t1: d, curve: "hold" as Curve };
  switch (type) {
    case "binaural":
      return { id: uniqueId(preset, "bb"), type, gainDb: -8,
        segments: [{ ...seg, carrierHz: [200, 200], beatHz: [10, 10] }] };
    case "monaural":
      return { id: uniqueId(preset, "mono"), type, gainDb: -10,
        segments: [{ ...seg, carrierHz: [300, 300], beatHz: [8, 8] }] };
    case "isochronic":
      return { id: uniqueId(preset, "iso"), type, gainDb: -12, dutyCycle: 0.5,
        gateShape: "raisedCosine", segments: [{ ...seg, carrierHz: [180, 180], beatHz: [10, 10] }] };
    case "noise":
      return { id: uniqueId(preset, "noise"), type, color: "brown", gainDb: -18 };
    case "ambience":
      return { id: uniqueId(preset, "amb"), type, asset: "rain_soft", gainDb: -18, loop: true };
    case "voice":
      return { id: uniqueId(preset, "voice"), type, source: "user://voice.mp3", gainDb: -8, startSec: 0 };
  }
}

/** Proportionally rescale all times when meta.durationSec changes. */
function rescaleDuration(preset: Preset, newDuration: number): void {
  const k = newDuration / preset.meta.durationSec;
  for (const layer of preset.layers) {
    if (isSegmentedLayer(layer)) {
      for (const s of layer.segments) {
        s.t0 = Math.round(s.t0 * k * 10) / 10;
        s.t1 = Math.round(s.t1 * k * 10) / 10;
      }
      layer.segments[0]!.t0 = 0;
      layer.segments[layer.segments.length - 1]!.t1 = newDuration;
    } else if (layer.type === "noise" && layer.envelope) {
      for (const p of layer.envelope) p.t = Math.round(p.t * k * 10) / 10;
    } else if (layer.type === "voice" && layer.startSec !== undefined) {
      layer.startSec = Math.round(layer.startSec * k * 10) / 10;
    }
  }
  preset.meta.durationSec = newDuration;
}

export class Panels {
  constructor(
    private readonly root: HTMLElement,
    private readonly store: Store,
  ) {
    store.subscribe((_s, what) => {
      if (what !== "transport") this.render();
    });
  }

  render(): void {
    const { preset, selection, errors } = this.store.state;
    this.root.replaceChildren();

    // Errors first — always visible when the preset is invalid.
    if (errors.length > 0) {
      this.root.append(
        h("div", { class: "errors" }, ...errors.map((e) => h("div", {}, e))),
      );
    }

    this.root.append(this.metaSection(preset));
    this.root.append(this.layersSection(preset, selection));

    if (selection?.kind === "segment") {
      const layer = preset.layers.find((l) => l.id === selection.layerId);
      if (layer && isSegmentedLayer(layer) && layer.segments[selection.segIndex]) {
        this.root.append(this.segmentSection(layer.id, selection.segIndex));
      }
    } else if (selection?.kind === "layer") {
      const layer = preset.layers.find((l) => l.id === selection.layerId);
      if (layer) this.root.append(this.layerSection(layer));
    }

    this.root.append(this.masterSection(preset));
  }

  private update(mutate: (p: Preset) => void): void {
    this.store.updatePreset(mutate);
  }

  private metaSection(preset: Preset): HTMLElement {
    return h(
      "section",
      {},
      h("h3", {}, "Preset"),
      textField("Name", preset.meta.name, (v) => this.update((p) => void (p.meta.name = v))),
      selField("Goal", preset.meta.goal, GOALS, (v) => this.update((p) => void (p.meta.goal = v))),
      numField("Duration (s)", preset.meta.durationSec, (v) => {
        if (v > 0) this.update((p) => rescaleDuration(p, v));
      }),
    );
  }

  private layersSection(preset: Preset, selection: Store["state"]["selection"]): HTMLElement {
    const list = h("div", { class: "layer-list" });
    for (const layer of preset.layers) {
      const row = h(
        "div",
        { class: "layer-row" + (selection && selection.kind !== "master" && selection.layerId === layer.id ? " sel" : "") },
        h("span", { class: "layer-name" }, `${layer.id} · ${layer.type}`),
      );
      const del = h("button", { class: "mini", title: "delete layer" }, "✕");
      del.addEventListener("click", (e) => {
        e.stopPropagation();
        this.update((p) => void p.layers.splice(p.layers.indexOf(layer), 1));
        this.store.select(null);
      });
      row.append(del);
      row.addEventListener("click", () => this.store.select({ kind: "layer", layerId: layer.id }));
      list.append(row);
    }

    const typeSel = h("select", {});
    for (const t of ["binaural", "monaural", "isochronic", "noise", "ambience", "voice"] as const) {
      typeSel.append(h("option", { value: t }, t));
    }
    const addBtn = h("button", {}, "+ add layer");
    addBtn.addEventListener("click", () => {
      const layer = defaultLayer(typeSel.value as LayerType, this.store.state.preset);
      this.update((p) => void p.layers.push(layer));
      this.store.select({ kind: "layer", layerId: layer.id });
    });

    return h("section", {}, h("h3", {}, "Layers"), list, h("div", { class: "row" }, typeSel, addBtn));
  }

  private layerSection(layer: Layer): HTMLElement {
    const sec = h("section", {}, h("h3", {}, `Layer ${layer.id}`));
    const id = layer.id;
    const find = (p: Preset) => p.layers.find((l) => l.id === id);

    sec.append(
      numField("Gain (dB)", layer.gainDb, (v) => this.update((p) => void (find(p)!.gainDb = v))),
      numField("Pan (−1..1)", layer.pan ?? 0, (v) => this.update((p) => void (find(p)!.pan = v)), "0.1"),
    );

    if (isSegmentedLayer(layer)) {
      sec.append(
        selField("Waveform", layer.waveform ?? "sine", WAVEFORMS, (v) =>
          this.update((p) => void ((find(p) as typeof layer).waveform = v)),
        ),
      );
      if (layer.type === "isochronic") {
        sec.append(
          numField("Duty cycle", layer.dutyCycle, (v) =>
            this.update((p) => void ((find(p) as typeof layer).dutyCycle = v)), "0.05"),
        );
      }
    } else if (layer.type === "noise") {
      sec.append(
        selField("Color", layer.color, COLORS, (v) =>
          this.update((p) => void ((find(p) as typeof layer).color = v))),
        this.envelopeEditor(id),
      );
    } else if (layer.type === "ambience") {
      const ids = AMBIENCE_MANIFEST.map((e) => e.id);
      sec.append(
        selField(
          "Sound",
          ids.includes(layer.asset) ? layer.asset : ids[0]!,
          ids,
          (v) => this.update((p) => void ((find(p) as typeof layer).asset = v)),
        ),
      );
    } else if (layer.type === "voice") {
      sec.append(
        textField("Source", layer.source, (v) =>
          this.update((p) => void ((find(p) as typeof layer).source = v))),
        h("div", { class: "field-hint" },
          "user://file (upload) · clv:warm:your words (AI voice, needs sign-in) · clv:deep:…"),
        numField("Start (s)", layer.startSec ?? 0, (v) =>
          this.update((p) => void ((find(p) as typeof layer).startSec = v))),
        // 0 clears the repeat (schema minimum is 1 s when present).
        numField("Repeat every (s, 0 = off)", layer.repeatEverySec ?? 0, (v) =>
          this.update((p) => {
            const l = find(p) as typeof layer;
            if (v > 0) l.repeatEverySec = v;
            else delete l.repeatEverySec;
          })),
        this.duckingEditor(id),
      );
    }
    return sec;
  }

  private envelopeEditor(layerId: string): HTMLElement {
    const layer = this.store.state.preset.layers.find((l) => l.id === layerId);
    if (!layer || layer.type !== "noise") return h("div");
    const wrap = h("div", { class: "subsection" }, h("h4", {}, "Envelope (dB over time)"));
    const points = layer.envelope ?? [];
    points.forEach((pt, i) => {
      const row = h("div", { class: "row" });
      row.append(
        numField("t", pt.t, (v) => this.update((p) => {
          const l = p.layers.find((x) => x.id === layerId);
          if (l?.type === "noise" && l.envelope) l.envelope[i]!.t = v;
        })),
        numField("dB", pt.gainDb, (v) => this.update((p) => {
          const l = p.layers.find((x) => x.id === layerId);
          if (l?.type === "noise" && l.envelope) l.envelope[i]!.gainDb = v;
        })),
      );
      const del = h("button", { class: "mini" }, "✕");
      del.addEventListener("click", () => this.update((p) => {
        const l = p.layers.find((x) => x.id === layerId);
        if (l?.type === "noise" && l.envelope) {
          l.envelope.splice(i, 1);
          if (l.envelope.length === 0) delete l.envelope;
        }
      }));
      row.append(del);
      wrap.append(row);
    });
    const add = h("button", {}, "+ point");
    add.addEventListener("click", () => this.update((p) => {
      const l = p.layers.find((x) => x.id === layerId);
      if (l?.type !== "noise") return;
      const last = l.envelope?.[l.envelope.length - 1];
      const t = last ? last.t + 10 : 0;
      (l.envelope ??= []).push({ t: Math.min(t, p.meta.durationSec), gainDb: last?.gainDb ?? l.gainDb });
    }));
    wrap.append(add);
    return wrap;
  }

  private duckingEditor(layerId: string): HTMLElement {
    const layer = this.store.state.preset.layers.find((l) => l.id === layerId);
    if (!layer || layer.type !== "voice") return h("div");
    const wrap = h("div", { class: "subsection" }, h("h4", {}, "Ducking"));
    const enabled = h("input", { type: "checkbox" });
    enabled.checked = layer.ducking !== undefined;
    enabled.addEventListener("change", () => this.update((p) => {
      const l = p.layers.find((x) => x.id === layerId);
      if (l?.type !== "voice") return;
      if (enabled.checked) l.ducking = { targetLayerTypes: ["noise", "ambience"], duckDb: -6 };
      else delete l.ducking;
    }));
    wrap.append(h("label", { class: "field-inline" }, enabled, "enabled"));
    if (layer.ducking) {
      for (const t of DUCKABLE) {
        const cb = h("input", { type: "checkbox" });
        cb.checked = layer.ducking.targetLayerTypes.includes(t);
        cb.addEventListener("change", () => this.update((p) => {
          const l = p.layers.find((x) => x.id === layerId);
          if (l?.type !== "voice" || !l.ducking) return;
          const set = new Set(l.ducking.targetLayerTypes);
          if (cb.checked) set.add(t);
          else set.delete(t);
          l.ducking.targetLayerTypes = [...set];
        }));
        wrap.append(h("label", { class: "field-inline" }, cb, t));
      }
      wrap.append(
        numField("Duck (dB)", layer.ducking.duckDb, (v) => this.update((p) => {
          const l = p.layers.find((x) => x.id === layerId);
          if (l?.type === "voice" && l.ducking) l.ducking.duckDb = v;
        })),
      );
    }
    return wrap;
  }

  private segmentSection(layerId: string, segIndex: number): HTMLElement {
    const layer = this.store.state.preset.layers.find((l) => l.id === layerId);
    if (!layer || !isSegmentedLayer(layer)) return h("div");
    const seg = layer.segments[segIndex]!;
    type Seg = typeof seg;
    type SegLayer = import("../../schema/types.ts").SegmentedLayer;
    const mutSeg = (fn: (s: Seg, l: SegLayer, p: Preset) => void) =>
      this.update((p) => {
        const l = p.layers.find((x) => x.id === layerId);
        if (l && isSegmentedLayer(l) && l.segments[segIndex]) fn(l.segments[segIndex]!, l, p);
      });

    const sec = h("section", {}, h("h3", {}, `Segment ${segIndex + 1} of ${layer.id}`));
    const t0Field = numField("Start (s)", seg.t0, (v) => mutSeg((s, l) => {
      const prev = l.segments[segIndex - 1];
      if (prev) { prev.t1 = v; s.t0 = v; }
    }));
    if (segIndex === 0) (t0Field.querySelector("input") as HTMLInputElement).disabled = true;
    const t1Field = numField("End (s)", seg.t1, (v) => mutSeg((s, l) => {
      const next = l.segments[segIndex + 1];
      s.t1 = v;
      if (next) next.t0 = v;
    }));
    if (segIndex === layer.segments.length - 1)
      (t1Field.querySelector("input") as HTMLInputElement).disabled = true;

    sec.append(
      t0Field,
      t1Field,
      numField("Carrier start (Hz)", seg.carrierHz[0], (v) => mutSeg((s) => void (s.carrierHz[0] = v))),
      numField("Carrier end (Hz)", seg.carrierHz[1], (v) => mutSeg((s) => void (s.carrierHz[1] = v))),
      numField("Beat start (Hz)", seg.beatHz[0], (v) => mutSeg((s) => void (s.beatHz[0] = v)), "0.5"),
      numField("Beat end (Hz)", seg.beatHz[1], (v) => mutSeg((s) => void (s.beatHz[1] = v)), "0.5"),
      selField("Curve", seg.curve, CURVES, (v) => mutSeg((s) => void (s.curve = v))),
    );

    const split = h("button", {}, "split at midpoint");
    split.addEventListener("click", () => mutSeg((s, l) => {
      const mid = Math.round(((s.t0 + s.t1) / 2) * 10) / 10;
      const midCarrier = (s.carrierHz[0] + s.carrierHz[1]) / 2;
      const midBeat = (s.beatHz[0] + s.beatHz[1]) / 2;
      const second = {
        t0: mid, t1: s.t1,
        carrierHz: [midCarrier, s.carrierHz[1]] as [number, number],
        beatHz: [midBeat, s.beatHz[1]] as [number, number],
        curve: s.curve,
      };
      s.t1 = mid;
      s.carrierHz = [s.carrierHz[0], midCarrier];
      s.beatHz = [s.beatHz[0], midBeat];
      l.segments.splice(segIndex + 1, 0, second);
    }));
    const merge = h("button", {}, "merge into previous");
    if (segIndex === 0) merge.disabled = true;
    merge.addEventListener("click", () => {
      this.update((p) => {
        const l = p.layers.find((x) => x.id === layerId);
        if (!l || !isSegmentedLayer(l)) return;
        const prev = l.segments[segIndex - 1]!;
        const cur = l.segments[segIndex]!;
        prev.t1 = cur.t1;
        prev.carrierHz = [prev.carrierHz[0], cur.carrierHz[1]];
        prev.beatHz = [prev.beatHz[0], cur.beatHz[1]];
        l.segments.splice(segIndex, 1);
      });
      this.store.select({ kind: "segment", layerId, segIndex: segIndex - 1 });
    });
    sec.append(h("div", { class: "row" }, split, merge));
    return sec;
  }

  private masterSection(preset: Preset): HTMLElement {
    const limiter = h("input", { type: "checkbox" });
    limiter.checked = preset.master.limiter;
    limiter.addEventListener("change", () =>
      this.update((p) => void (p.master.limiter = limiter.checked)));
    const fallback = h("input", { type: "checkbox" });
    fallback.checked = preset.allowFallback === true;
    fallback.addEventListener("change", () =>
      this.update((p) => void (p.allowFallback = fallback.checked)));
    const fallbackLabel = h("label", { class: "field-inline" }, fallback, "auto-adapt to speakers");
    fallbackLabel.title =
      "Let players swap binaural layers for a speaker-safe technique (same beat) when no stereo headphones are connected.";
    return h(
      "section",
      {},
      h("h3", {}, "Master"),
      numField("Gain (dB)", preset.master.gainDb, (v) => this.update((p) => void (p.master.gainDb = v))),
      numField("Fade in (s)", preset.master.fadeInSec, (v) => this.update((p) => void (p.master.fadeInSec = v))),
      numField("Fade out (s)", preset.master.fadeOutSec, (v) => this.update((p) => void (p.master.fadeOutSec = v))),
      h("label", { class: "field-inline" }, limiter, "limiter"),
      fallbackLabel,
    );
  }
}
